import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DropdownList from "react-widgets/DropdownList";
import { useTranslation } from "react-i18next";
import { Form, Button, Modal } from "react-bootstrap";
import { FormSelect } from "../../../../../Components/Buttons/FormSelect/FormSelect";
import TableTemplate from "../../../../../Components/Table/TableTemplate";
import { WebRoutes } from "../../../../../Constants";
import {
  getLanguages,
  addLanguageTranslationByCode,
  deleteLanguageTranslationByCode,
  updateLanguageByCode,
  exportLanguageCsv,
} from "../../../../../Backend";
import "./LanguagesAdministrationPage.scss";

export function LanguagesAdministrationPage() {
  const tableTemplateRef = useRef(null);
  const navigate = useNavigate();
  const [t, i18n] = useTranslation();
  const [languageCode, setLanguageCode] = useState("es");
  const [activeLanguageCode, setActiveLanguageCode] = useState("es");
  const [languages, setLanguages] = useState([]);
  const [newTranslation, setNewTranslation] = useState({ key: "", value: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [columns] = useState([
    {
      name: "key",
      headerCaption: t("Key"),
      sortName: "translationKey.Name",
      filterName: "translationKey.Name",
      editName: "translationKey.Name",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "value",
      headerCaption: t("Value"),
      sortName: "Value",
      filterName: "Value",
      editName: "Value",
      canSort: true,
      canFilter: true,
      canEdit: true,
    },
  ]);

  useEffect(() => {
    getLanguagesList();
  }, []);

  const getLanguagesList = async () => {
    const data = await getLanguages([{ key: "isTranslated", value: "true" }]);

    if (data.success) {
      let obj = data.data.map((x) => {
        return { id: x.languageCode, name: x.name };
      });
      setLanguages(obj);
      setLanguageCode(i18n.language);
      setActiveLanguageCode(i18n.language)
    }
  };

  const updateLanguage = async (row) => {
    let data = await updateLanguageByCode(languageCode, row.key, row.value);

    if (data.success) {
      tableTemplateRef.current.getItems();
    }
  };

  const deleteLanguage = async (row) => {
    let data = await deleteLanguageTranslationByCode(languageCode, row.key);
    if (data.success) {
      tableTemplateRef.current.getItems();
    }
  };

  const addTranslation = async () => {
    let data = await addLanguageTranslationByCode(
      languageCode,
      newTranslation.key,
      newTranslation.value
    );
    if (data.success) {
      tableTemplateRef.current.getItems();
    }
  };

  const exportLanguageFile = async () => {
    await exportLanguageCsv(languageCode, "Translations-" + languageCode);
  };
  
  return (
    <div className="languages-page">
      <TableTemplate
        ref={tableTemplateRef}
        title={t("LanguagesSettings")}
        endpoint={"/language/translations/" + languageCode + "/all"}
        columns={columns}
        uniqueId="key"
        addItem={() => setShowAddModal(!showAddModal)}
        onUpdate={updateLanguage}
        onDelete={deleteLanguage}
        customButtons={[
          {
            name: "Import",
            action: () =>
              navigate(WebRoutes.ImportOverviewPage, {
                state: {
                  impOption: "translation",
                  languageCode: languageCode,
                },
              }),
            icon: "publish",
          },
          {
            name: "Export",
            action: () => exportLanguageFile(),
            icon: "get_app",
          },
        ]}
        extraFilters={[
          {
            name: "language",
            headerCaption: t("Language"),
            value: "",
            component: (
              <FormSelect
                placeholder={t("Language")}
                type="text"
                options={languages}
                value={languageCode}
                change={(x) => setLanguageCode(x)}
              />
            ),
          },
        ]}
        paginator={true}
      />
      {showAddModal && (
        <Modal
          className="add-language-modal"
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
        >
          <Modal.Header className="header" closeButton title={t("AddLanguage")}>
            <div className="header-wrapper">
              <div className="title">{t("AddLanguage")}</div>
              <DropdownList
                data={languages}
                dataKey="id"
                textField="name"
                defaultValue={activeLanguageCode}
                onChange={(x) => setActiveLanguageCode(x.id)}
              />
            </div>
          </Modal.Header>
          <Modal.Body className="body">
            {columns.map((column, i) => (
              <div className="row-wrapper" key={"filter-" + column.name}>
                <div className="label">{column.headerCaption}</div>
                <Form.Control
                  value={
                    newTranslation[column.name]
                      ? newTranslation[column.name]
                      : ""
                  }
                  onChange={(x) => {
                    setNewTranslation({
                      ...newTranslation,
                      [column.name]: x.target.value,
                    });
                  }}
                />
              </div>
            ))}
            <div className="button-wrapper">
              <Button
                className="button-cancel"
                onClick={() => {
                  setShowAddModal(false);
                  setNewTranslation({});
                }}
              >
                {t("Cancel")}
              </Button>
              <Button
                className="button"
                onClick={() => {
                  setShowAddModal(false);
                  setNewTranslation({});
                  addTranslation();
                }}
              >
                {t("Save")}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
